import React, { createContext, useReducer, useContext } from 'react';
import { StrategyConfig, TradingSignal } from '../types';

// Define the state interface
interface StrategyState {
  config: StrategyConfig;
  signals: TradingSignal[];
  loading: boolean;
  error: string | null;
}

// Define the action types
type StrategyAction =
  | { type: 'SET_CONFIG'; payload: StrategyConfig }
  | { type: 'SET_SIGNALS'; payload: TradingSignal[] }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null };

// Create the initial state
const initialState: StrategyState = {
  config: {
    capeLookbackYears: 8,
    capeEmaSpan: 4,
    pnfPatternType: 'quad-top',
    pnfBoxSizePercent: 0.01,
    pnfReversalBoxes: 3,
    capeThreshold: { buy: 20, sell: 30 },
    fscoreThreshold: { buy: 7, sell: 3 },
  },
  signals: [],
  loading: false,
  error: null,
};

// Create the context
const StrategyContext = createContext<{
  state: StrategyState;
  dispatch: React.Dispatch<StrategyAction>;
}>({
  state: initialState,
  dispatch: () => null,
});

// Create the reducer
const strategyReducer = (state: StrategyState, action: StrategyAction): StrategyState => {
  switch (action.type) {
    case 'SET_CONFIG':
      return { ...state, config: action.payload };
    case 'SET_SIGNALS':
      return { ...state, signals: action.payload };
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload };
    default:
      return state;
  }
};

// Create the provider component
export const StrategyProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(strategyReducer, initialState);

  return (
    <StrategyContext.Provider value={{ state, dispatch }}>
      {children}
    </StrategyContext.Provider>
  );
};

// Create a custom hook to use the context
export const useStrategy = () => useContext(StrategyContext); 